import { type Feature } from 'ol';
import { type Geometry } from 'ol/geom';
import { get as getProjection } from 'ol/proj';
import type VectorSource from 'ol/source/Vector';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useFillableSubmissionContext } from '../../../../FillableSubmissionContext.tsx';
import { type IMapWidget } from '../../IMapWidget.ts';
import {
  type IMapLayer,
  isIdentifyLayerVisible,
} from '../../layers/MapLayer.ts';
import { MapWidgetLogic } from '../../MapWidgetLogic.ts';
import { createIntersectionVectorSource } from '../intersection/utils/createIntersectionVectorSource.ts';
import { getIdentifyLayersInfo } from './getIdentifyLayersInfo.ts';
import {
  type IIdentifyFeature,
  type IIdentifyLayerResult,
} from './IIdentifyResult.ts';

export interface IMapIdentifyManagerOptions {
  widget: IMapWidget;
}

export interface IMapIdentifyManagerResult {
  executeIdentify: (geometry: Geometry) => void;
  /** `null` = nessuna interrogazione eseguita, `[]` = interrogazione senza risultati. */
  results: IIdentifyLayerResult[] | null;
  isLoading: boolean;
  clearResults: () => void;
  initIdentifyManager: () => void;
  /** `true` se esiste almeno un layer interrogabile visibile nel contesto corrente. */
  hasIdentifyLayers: boolean;
}

/**
 * Gestisce l'interrogazione (identify) dei layer configurati.
 * Riusa lo stesso pattern di useMapIntersectionManager:
 * VectorSource con URL ArcGIS REST + loadFeatures + featuresloadend.
 */
export function useMapIdentifyManager(
  options: IMapIdentifyManagerOptions,
): IMapIdentifyManagerResult {
  const { widget } = options;
  const { isBackoffice } = useFillableSubmissionContext();

  const identifyLayers = useMemo(
    () =>
      MapWidgetLogic.isAdvancedMapWidgetEnabled()
        ? getIdentifyLayersInfo(widget).filter(({ layer }) =>
            isIdentifyLayerVisible(layer, isBackoffice ?? false),
          )
        : [],
    [widget, isBackoffice],
  );

  const [results, setResults] = useState<IIdentifyLayerResult[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Id dell'interrogazione corrente. Ogni `executeIdentify` lo incrementa: i
   * settle di richieste superate (o annullate da `clearResults`) vengono
   * ignorati, così una richiesta obsoleta non pubblica risultati né spegne lo
   * spinner al posto di quella corrente.
   */
  const requestIdRef = useRef(0);

  const identifySourcesRef = useRef<Map<number, VectorSource>>(new Map());

  /** Funzioni di unbind dei listener registrati, per il cleanup. */
  const listenerKeysRef = useRef<Array<() => void>>([]);

  /**
   * Estrae gli attributi configurati dalle feature caricate
   * e produce il risultato per un singolo layer.
   */
  const buildLayerResult = (
    layer: IMapLayer,
    source: VectorSource,
  ): IIdentifyLayerResult => {
    const identify = layer.identify;
    const configuredAttributes = identify?.attributes ?? [];
    const maxResults = identify?.maxResults ?? 100;
    const features = source.getFeatures();

    const identifyFeatures: IIdentifyFeature[] = features
      .slice(0, maxResults)
      .map((feature: Feature) => {
        const props = feature.getProperties();
        const attributes: Record<string, unknown> = {};
        for (const attr of configuredAttributes) {
          attributes[attr] = props?.[attr] ?? '';
        }
        return { attributes };
      });

    return {
      layerName: layer.name,
      // Titolo facoltativo: se vuoto si usa il nome del livello.
      layerTitle: identify?.title || layer.name,
      features: identifyFeatures,
      aliases: identify?.aliases,
    };
  };

  /**
   * Se il layer interrogato ha restituito feature, ne accumula il risultato
   * e svuota la source. Estratto da `settle` per contenerne la complessità.
   */
  const collectLayerResult = (
    layer: IMapLayer,
    source: VectorSource,
    accumulator: IIdentifyLayerResult[],
  ) => {
    const result = buildLayerResult(layer, source);
    source.clear();
    if (result.features.length > 0) {
      accumulator.push(result);
    }
  };

  /**
   * Rimuove tutti i listener di caricamento registrati
   * e svuota il tracking.
   */
  const removeAllListeners = () => {
    for (const unbind of listenerKeysRef.current) {
      unbind();
    }
    listenerKeysRef.current = [];
  };

  /**
   * Crea le VectorSource per ogni layer identify; un layer con config
   * incompleta resta fuori dalla Map e `executeIdentify` lo ignora.
   * Deve essere chiamata durante l'inizializzazione della mappa.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: chiamata una sola volta all'init della mappa
  const initIdentifyManager = useCallback(() => {
    removeAllListeners();

    const sources = new Map<number, VectorSource>();
    for (const { layer, index } of identifyLayers) {
      const source = createIntersectionVectorSource({
        type: layer.type,
        url: layer.url,
        wfs: layer.wfs,
      });
      if (source) sources.set(index, source);
    }
    identifySourcesRef.current = sources;
  }, [identifyLayers]);

  /**
   * Avvia l'interrogazione su tutti i layer identify abilitati.
   * Rimuove i listener precedenti e ne registra di nuovi one-shot
   * per ogni source, così eventuali risposte in-flight della
   * richiesta precedente vengono ignorate.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: buildLayerResult e removeAllListeners sono funzioni interne che leggono solo da ref/parametri
  const executeIdentify = useCallback(
    (geometry: Geometry) => {
      const extent = geometry.getExtent();
      const projection = getProjection('EPSG:3857');
      if (!projection) return;

      removeAllListeners();

      const requestId = ++requestIdRef.current;
      setIsLoading(true);

      const activeLayers = identifyLayers.filter(({ index }) =>
        identifySourcesRef.current.has(index),
      );

      if (activeLayers.length === 0) {
        setIsLoading(false);
        return;
      }

      // Contatore e accumulatore locali alla singola interrogazione: nessuno
      // stato condiviso tra richieste diverse.
      let pending = activeLayers.length;
      const accumulated: IIdentifyLayerResult[] = [];

      for (const { layer, index } of activeLayers) {
        const source = identifySourcesRef.current.get(index);
        if (!source) continue;

        // refresh() svuota feature ED extent già caricate: garantisce che
        // loadFeatures riparta davvero (anche sulla stessa area) e quindi
        // emetta sempre featuresloadend/error → il contatore non resta appeso.
        source.refresh();

        // One-shot: su load o errore il layer viene "chiuso" e i listener
        // rimossi. Un layer in errore (es. CORS/404) non deve bloccare
        // la pubblicazione dei risultati degli altri layer.
        const settle = (loaded: boolean) => {
          unbind();

          // Interrogazione superata o annullata: ignora senza toccare lo stato.
          if (requestId !== requestIdRef.current) return;

          if (loaded) collectLayerResult(layer, source, accumulated);

          pending -= 1;
          if (pending > 0) return;

          setResults([...accumulated]);
          setIsLoading(false);
        };

        const onLoadEnd = () => settle(true);
        const onLoadError = () => settle(false);
        const unbind = () => {
          source.un('featuresloadend', onLoadEnd);
          source.un('featuresloaderror', onLoadError);
        };

        source.on('featuresloadend', onLoadEnd);
        source.on('featuresloaderror', onLoadError);
        listenerKeysRef.current.push(unbind);

        source.loadFeatures(extent, 1, projection);
      }
    },
    [identifyLayers],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: removeAllListeners legge solo da ref
  const clearResults = useCallback(() => {
    removeAllListeners();
    // Invalida gli eventuali settle in volo dell'interrogazione corrente.
    requestIdRef.current += 1;
    setResults(null);
    setIsLoading(false);
  }, []);

  return {
    executeIdentify,
    results,
    isLoading,
    clearResults,
    initIdentifyManager,
    hasIdentifyLayers: identifyLayers.length > 0,
  };
}
